import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useQueryLoader,
} from "react-relay";
import { OrgSpacesQuery } from "./__generated__/OrgSpacesQuery.graphql";
import { Image, ScrollView } from "react-native";
import { OrgSpaceItem, useGetOrgSpaceItemHeight } from "./OrgSpaceItem";
import { OrgSpaceItem_storecastSpace$key } from "./__generated__/OrgSpaceItem_storecastSpace.graphql";
import {
  AHEmptyScreen,
  AHText,
  Box,
  ScaleFactorProvider,
  useLayoutContext,
} from "arkh";
import { OrgHomeScreenProps } from "../OrgHomeNavigation";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { Portal } from "@gorhom/portal";
import { useSharedValue } from "react-native-reanimated";
import { OrgSpacesComponent_workspace$key } from "./__generated__/OrgSpacesComponent_workspace.graphql";
import {
  ORG_SPACE_NOTE_EDIT_QUERY,
  OrgSpaceNoteEdit,
  OrgSpaceNoteEditQuery,
} from "./OrgSpaceNoteEdit";
import { useOrgSpaceUnitSize } from "./useOrgSpaceUnitSize";
import { FormattedMessage } from "react-intl";

import { useOrgHomeDetailContainerStyle } from "../useOrgHomeDetailContainerStyle";
import { OrgButton } from "../OrgButton";
import { ORG_HOME_PORTAL_NAME } from "../ORG_HOME_PORTAL_NAME";
import { useWorkspaceId, WorkspaceWritable } from "../workspaceId";
import { WorkspaceDetailNavigationHeader } from "../WorkspaceDetailNavigationHeader";

type OrgSpaceItemData = OrgSpaceItem_storecastSpace$key & { id: string };

const ORG_SPACES_DESCRIPTION = (
  <FormattedMessage
    defaultMessage="A Storecast Space is a dedicated space on Storecast that is controlled centrally within your workspace. Here you can edit widgets and update content. All changes are automatically synced in real-time to active Storecasts in your units."
    description="Org spaces description"
  />
);

function OrgSpacesEmpty() {
  return (
    <AHEmptyScreen
      title={
        <FormattedMessage
          defaultMessage="Create a Storecast Space"
          description="Org spaces Empty Screen Title"
        />
      }
      subtitle={ORG_SPACES_DESCRIPTION}
      illustration={
        <Box
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <Image
            style={{ height: 220, backgroundColor: "organge" }}
            resizeMode="contain"
            source={require("./OrgSpaceIllustration.png")}
          />
        </Box>
      }
    />
  );
}

function OrgSpacesComponent(props: {
  newlyCreatedSpaceId?: string;
  workspace: OrgSpacesComponent_workspace$key;
  openWidgetSettings: (_: { widgetId: string; __typename: string }) => void;
  onAddWidget: (_: { spaceId: string; y: number; h: number }) => void;
  onShowStatus: (spaceId: string, workspaceId: string) => void;
  onEditSpace(spaceId: string): void;
  onOpenFile: (_: { fileId: string; pageId: string }) => void;
}) {
  const [editingSpaceId, setEditingSpaceId] = React.useState<string | null>(
    null
  );
  const hiddenWidgetId = useSharedValue<string | null>(null);
  const nextHiddenWidgetIdValue = useRef(false);
  if (nextHiddenWidgetIdValue.current) {
    // hmm not safe?
    // Component not side effect free
    hiddenWidgetId.value = null;
    nextHiddenWidgetIdValue.current = false;
  }

  const [noteEditQueryRef, loadNoteEditQuery, disposeNoteEditQuery] =
    useQueryLoader<OrgSpaceNoteEditQuery>(ORG_SPACE_NOTE_EDIT_QUERY);
  const posRef = useRef({ x: 0, y: 0 });
  const pageIdRef = useRef("");
  const onNoteEdit = useCallback(
    (widgetId: string, pageId: string, x: number, y: number) => {
      pageIdRef.current = pageId;
      posRef.current = { x, y };
      console.log("on note edit");
      loadNoteEditQuery({ id: widgetId }, { fetchPolicy: "store-and-network" });
    },
    [loadNoteEditQuery]
  );

  const contentContainerStyle = useOrgHomeDetailContainerStyle();

  const workspace = useFragment(
    graphql`
      fragment OrgSpacesComponent_workspace on Workspace {
        id
        spaces {
          id
          name
          height
          ...OrgSpaceItem_storecastSpace
        }
      }
    `,
    props.workspace
  );

  const getOrgSpaceItemHeight = useGetOrgSpaceItemHeight();

  const renderItem = useCallback(
    ({ item }: { item: OrgSpaceItemData }) => (
      <OrgSpaceItem
        onEditSpace={props.onEditSpace}
        hiddenWidgetId={hiddenWidgetId}
        space={item}
        isEditing={editingSpaceId === item.id}
        setIsEditing={(isEditing) => {
          setEditingSpaceId(isEditing ? item.id : null);
        }}
        onShowStatus={(spaceId) => {
          props.onShowStatus(spaceId, workspace.id);
        }}
        openWidgetSettings={props.openWidgetSettings}
        onAddWidget={props.onAddWidget}
        onEvent={({ event, x, y }) => {
          switch (event.kind) {
            case "NoteSelect": {
              onNoteEdit(event.widgetId, event.pageId, x, y);
              break;
            }
            case "FileOpen": {
              props.onOpenFile({
                fileId: event.fileId,
                pageId: event.pageId,
              });
              break;
            }
          }
        }}
      />
    ),
    [editingSpaceId, hiddenWidgetId, onNoteEdit, props, workspace.id]
  );

  const ListEmptyComponent = useMemo(() => {
    return <OrgSpacesEmpty />;
  }, []);

  const unitSize = useOrgSpaceUnitSize();

  const spaces = useMemo(
    () =>
      workspace.spaces
        .slice()
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [workspace.spaces]
  );

  const layout = useLayoutContext();
  const scrollRef = useRef<ScrollView | null>(null);

  useEffect(() => {
    if (props.newlyCreatedSpaceId !== undefined) {
      const index = spaces.findIndex(
        (space) => space.id === props.newlyCreatedSpaceId
      );
      setEditingSpaceId(props.newlyCreatedSpaceId);
      if (index !== -1) {
        const y = spaces.slice(0, index).reduce((acc, space, i) => {
          return (
            acc + layout.space.medium + getOrgSpaceItemHeight(space.height)
          );
        }, 0);
        setTimeout(() => {
          scrollRef.current?.scrollTo({
            y,
          });
        }, 300);
      }
    }
  }, [
    getOrgSpaceItemHeight,
    layout.space.medium,
    props.newlyCreatedSpaceId,
    spaces,
    unitSize,
  ]);

  if (spaces.length === 0) {
    return ListEmptyComponent;
  }

  return (
    <>
      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        contentContainerStyle={contentContainerStyle}
      >
        {spaces.map((space, index) => (
          <Box key={space.id} spaceTop="medium">
            {renderItem({ item: space })}
          </Box>
        ))}
      </ScrollView>
      {noteEditQueryRef ? (
        <Portal hostName={ORG_HOME_PORTAL_NAME}>
          <ScaleFactorProvider value={unitSize / 236}>
            <OrgSpaceNoteEdit
              workspaceId={workspace.id}
              queryRef={noteEditQueryRef}
              pageId={pageIdRef.current}
              onClosed={() => {
                nextHiddenWidgetIdValue.current = true;
                disposeNoteEditQuery();
              }}
              onStart={() => {
                hiddenWidgetId.value = noteEditQueryRef.variables.id;
              }}
              unitSize={unitSize}
              left={posRef.current.x}
              top={posRef.current.y}
            />
          </ScaleFactorProvider>
        </Portal>
      ) : null}
    </>
  );
}

function InnerOrgSpaces({
  fetchKey,
  ...props
}: { fetchKey: string } & OrgHomeScreenProps<"OrgSpaces">) {
  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<OrgSpacesQuery>(
    graphql`
      query OrgSpacesQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          id
          ...OrgSpacesComponent_workspace
        }
      }
    `,
    {
      workspaceId,
    },
    { fetchKey, fetchPolicy: "store-and-network" }
  );

  const unitSize = useOrgSpaceUnitSize();

  const openWidgetSettings = useCallback(
    ({ widgetId, __typename }: { widgetId: string; __typename: string }) => {
      switch (__typename) {
        case "WidgetNote": {
          props.navigation.navigateDeprecated("WSNote", {
            widgetId,
          });
          break;
        }
        case "WidgetFile": {
          props.navigation.navigateDeprecated("WSFile", {
            widgetId,
            unitSize,
            isOrg: true,
            workspaceId,
          });
          break;
        }
      }
    },
    [props.navigation, unitSize, workspaceId]
  );

  return data.workspace ? (
    <OrgSpacesComponent
      {...props}
      onOpenFile={({ fileId, pageId }) => {
        props.navigation.navigateDeprecated("File", {
          id: fileId,
          pageId,
        });
      }}
      openWidgetSettings={openWidgetSettings}
      onShowStatus={(spaceId, workspaceId) => {
        props.navigation.navigateDeprecated("OrgSpaceActiveInfo", {
          spaceId,
          workspaceId,
        });
      }}
      onAddWidget={({
        spaceId,
        y,
        h,
      }: {
        spaceId: string;
        y: number;
        h: number;
      }) => {
        props.navigation.navigateDeprecated("OrgSpaceWidgetAdd", {
          position: { y, h, x: 0, c: 0, w: 2, active: true },
          spaceId,
          maxSize: {
            w: 2,
            h: h as 1 | 2 | 3 | 4,
          },
          workspaceId,
        });
      }}
      newlyCreatedSpaceId={props.route.params.newlyCreatedSpaceId}
      workspace={data.workspace}
      onEditSpace={(spaceId) => {
        props.navigation.navigateDeprecated("OrgSpaceEdit", {
          screen: "OrgSpaceEditMain",
          params: {
            spaceId,
          },
        });
      }}
    />
  ) : null;
}

export function OrgSpaces(props: OrgHomeScreenProps<"OrgSpaces">) {
  const intl_tokens = useIntlTokens();
  const workspaceId = useWorkspaceId();
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={<AHText type="subtitle">{intl_tokens.Spaces}</AHText>}
        right={
          <WorkspaceWritable>
            <OrgButton
              backgroundColor="primary"
              icon="add"
              tintColor="text-inverted"
              title={intl_tokens.Create}
              onPress={() => {
                props.navigation.setParams({
                  newlyCreatedSpaceId: undefined,
                });
                props.navigation.navigateDeprecated("OrgSpaceCreate", {
                  workspaceId,
                });
              }}
            />
          </WorkspaceWritable>
        }
      />
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => <InnerOrgSpaces {...props} fetchKey={fetchKey} />}
      </LazyQueryNetworkBoundrary>
    </>
  );
}
